import type { Point } from './types.js';

/**
 * Screen-space geometry helpers.
 *
 * Shared by the line-based drawings so hit testing and extension behave
 * identically across types.
 */

/** Pointer tolerance in pixels — comfortable to hit without being grabby. */
export const HIT_TOLERANCE = 6;
/** Radius of a handle dot, and the distance at which it captures the pointer. */
export const HANDLE_RADIUS = 5;
export const HANDLE_HIT_RADIUS = 9;

/** Distance from a point to the segment a-b. */
export const distanceToSegment = (point: Point, a: Point, b: Point): number => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) return Math.hypot(point.x - a.x, point.y - a.y);

  /* Projection of the point onto the segment, clamped to its ends. */
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lengthSquared));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
};

export const distanceToPoint = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

/**
 * Extends the line through a and b to the given horizontal bounds.
 *
 * Extension happens in screen space: projecting a far-future date back through the
 * time scale would require extrapolating the axis, whereas a straight line stays
 * straight under the linear mapping, so continuing it in pixels is both simpler
 * and exact.
 */
export const extendLine = (
  a: Point,
  b: Point,
  bounds: { left: number; right: number },
  extendLeft: boolean,
  extendRight: boolean,
): [Point, Point] => {
  if (a.x === b.x) {
    /* Vertical line: extension along x is meaningless. */
    return [a, b];
  }

  const slope = (b.y - a.y) / (b.x - a.x);
  const yAt = (x: number): number => a.y + slope * (x - a.x);

  const left = a.x <= b.x ? a : b;
  const right = a.x <= b.x ? b : a;

  const start = extendLeft ? { x: bounds.left, y: yAt(bounds.left) } : left;
  const end = extendRight ? { x: bounds.right, y: yAt(bounds.right) } : right;
  return [start, end];
};

/** Applies a stroke style, including the dash pattern. */
export const applyStroke = (
  ctx: CanvasRenderingContext2D,
  color: string,
  lineWidth: number,
  lineStyle: 'solid' | 'dashed' | 'dotted',
): void => {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.setLineDash(lineStyle === 'dashed' ? [7, 5] : lineStyle === 'dotted' ? [2, 4] : []);
};

/** Draws a filled handle dot with a contrasting ring. */
export const drawHandle = (ctx: CanvasRenderingContext2D, point: Point, color: string): void => {
  ctx.save();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.arc(point.x, point.y, HANDLE_RADIUS, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();
  ctx.restore();
};

/** Draws a label with a readable background plate. */
export const drawLabel = (
  ctx: CanvasRenderingContext2D,
  text: string,
  anchor: Point,
  options: { color: string; align?: 'left' | 'right' | 'center'; baseline?: 'top' | 'bottom' } = {
    color: '#2962ff',
  },
): void => {
  if (text.length === 0) return;

  const { color, align = 'left', baseline = 'bottom' } = options;
  ctx.save();
  ctx.setLineDash([]);
  ctx.font = '12px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

  const metrics = ctx.measureText(text);
  const paddingX = 6;
  const boxWidth = metrics.width + paddingX * 2;
  const boxHeight = 20;

  const x =
    align === 'right'
      ? anchor.x - boxWidth
      : align === 'center'
        ? anchor.x - boxWidth / 2
        : anchor.x;
  const y = baseline === 'top' ? anchor.y : anchor.y - boxHeight;

  ctx.fillStyle = color;
  ctx.globalAlpha = 0.92;
  ctx.beginPath();
  ctx.roundRect(x, y, boxWidth, boxHeight, 3);
  ctx.fill();

  ctx.globalAlpha = 1;
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  ctx.fillText(text, x + paddingX, y + boxHeight / 2 + 0.5);
  ctx.restore();
};
